// @flow

import * as React from 'react';
import { connect } from 'react-redux';
import { Query } from 'react-apollo';

import { localize } from '@ta/common.i18n';
import type { ViewportCategory } from '@ta/common.state';
import WindowScroll from '@ta/events.window-scroll';
import { sessionType } from '@ta/social.feed';
import { getAllowedTypes } from '@ta/social.feed-filter';
import { Overlay } from '@ta/overlays.pieces';
import { Icon } from '@ta/styleguide.icon';
import CheckNewFeedQuery from './CheckNewFeed.gql';

import styles from './CheckNewFeed.less';

const TOP_OFFSET = 50;
const FIXED_TOP = 120;

type ExternalProps = {|
  /* ref to the top of feed */
  topOfFeed: React.ElementRef<*>,
  /* call to reload the feed */
  reloadFeedCallback: () => void,
  /* Data necessary to check if there are new feed */
  placement: string,

  puid: string,
  locationId: number,
  latitude?: number,
  longitude?: number,
  feedIdToRestore: ?string,
  allowedTypes: ?Array<string>,
  ipGeo: number,
|};

type Props = {|
  ...ExternalProps,
  loggedInUserId: string,
  viewportCategory: ViewportCategory,
|};

type State = {
  checkNewFeed: boolean,
  promptStyle: { top: number, left: number },
};

type OverlayProps = {|
  /** position of the button */
  position: any,

  /** call to refresh the feed */
  refreshFeed: () => void,
|};

/** The CheckNewFeed UI element, does not contain any logic for positioning and display */
export const CheckNewFeedOverlay = ({ position, refreshFeed }: OverlayProps) => (
  <Overlay overlayType="background" position={position}>
    <a className={styles.text_container} onClick={refreshFeed}>
      <Icon name="arrow-up" className={styles.icon} />
      <span className={styles.text}>{localize('social_feed_prompt_refresh') /* New posts */}</span>
    </a>
  </Overlay>
);

class CheckNewFeed extends React.Component<Props, State> {
  state = {
    checkNewFeed: false,
    promptStyle: { top: 0, left: 0 },
  };

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.loggedInUserId && this.props.loggedInUserId && !this.state.checkNewFeed) {
      /* eslint-disable-next-line react/no-did-update-set-state */
      this.setState({
        checkNewFeed: true,
        promptStyle: this.calcPromptPosition(),
      });
    }
  }

  calcPromptPosition() {
    const parentBoundingBox = this.props.topOfFeed.current.getBoundingClientRect();
    const currentTop = parentBoundingBox.top;
    const parentWidth = parentBoundingBox.right - parentBoundingBox.left;
    const left = parentBoundingBox.left + parentWidth / 2;
    let top;

    if (currentTop > TOP_OFFSET) {
      top = currentTop + TOP_OFFSET;
    } else {
      top = FIXED_TOP;
    }

    return {
      top,
      left,
    };
  }

  refreshFeed() {
    this.setState(
      {
        checkNewFeed: false,
      },
      () => {
        this.props.reloadFeedCallback();

        // if we are lower in the feed, scroll to the top.
        if (this.props.topOfFeed.current.getBoundingClientRect().top < 0) {
          // eslint-disable-next-line no-restricted-globals
          window.scrollTo({
            top: this.props.topOfFeed.current.getBoundingClientRect().top,
            behavior: 'smooth',
          });
        }
      },
    );
  }

  updatePromptPosition() {
    if (this.state.checkNewFeed) {
      const position = this.calcPromptPosition();
      if (
        position.left !== this.state.promptStyle.left ||
        position.top !== this.state.promptStyle.top
      ) {
        this.setState({ promptStyle: this.calcPromptPosition() });
      }
    }
  }

  render() {
    const allowedTypes = getAllowedTypes(this.props.allowedTypes);
    if (!this.state.checkNewFeed) {
      return null;
    }

    return (
      <Query
        query={CheckNewFeedQuery}
        variables={{
          /* $FlowFixGraphql (WP-1357) */
          placement: this.props.placement,
          /* $FlowFixGraphql (WP-1357) */
          locationId: `${this.props.locationId}`,
          /* $FlowFixGraphql (WP-1357) */
          sessionType: sessionType(this.props.viewportCategory),
          puid: this.props.puid,
          /* $FlowFixGraphql (WP-1357) */
          allowedTypes,
          /* $FlowFixGraphql (WP-1357) */
          latitude: this.props.latitude || null,
          longitude: this.props.longitude || null,
          restoreFromFeedId: this.props.feedIdToRestore,
          /* $FlowFixGraphql (WP-1357) */
          ipGeo: `${this.props.ipGeo}`,
        }}
      >
        {({ loading, data }) => {
          if (
            !loading &&
            data &&
            data.socialFeedHasNewContent &&
            data.socialFeedHasNewContent.hasNewContent
          ) {
            const position = {
              position: 'fixed',
              top: `${this.state.promptStyle.top}px`,
              left: `${this.state.promptStyle.left}px`,
              transform: 'translateX(-50%)',
            };

            return (
              <React.Fragment>
                <CheckNewFeedOverlay
                  position={position}
                  refreshFeed={() => {
                    this.refreshFeed();
                  }}
                />
                <WindowScroll
                  callback={() => {
                    this.updatePromptPosition();
                  }}
                  throttle={50}
                />
              </React.Fragment>
            );
          }
          return null;
        }}
      </Query>
    );
  }
}

export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  viewportCategory: state.meta.device.viewportCategory,
  loggedInUserId: state.auth.loggedInUserId,
}))(CheckNewFeed);
